/**
 * Deck — one transport in front of two engines.
 *
 * Everything above this layer talks in {@link Track}s and never has to know
 * whether the sound is coming from the generative synth or a YouTube iframe.
 */

import { Emitter } from '../lib/emitter.ts';
import { clamp } from '../lib/math.ts';
import type { Track, TrackSource } from '../types/media.ts';
import { bridgeEngine, type DeckEvents } from './bridge.ts';
import { DemoEngine } from './demo/engine.ts';
import type { Engine, PitchResult } from './engine.ts';
import { Queue } from './queue.ts';
import { YouTubeEngine } from './youtube.ts';

export class Deck extends Emitter<DeckEvents> {
  readonly demo = new DemoEngine();
  readonly yt = new YouTubeEngine('yt-mount');
  readonly playlist = new Queue();

  track: Track | null = null;
  volume = 0.85;
  muted = false;

  #pitch = 0;

  constructor() {
    super();
    this.#bridge('demo', this.demo);
    this.#bridge('youtube', this.yt);
  }

  get queue(): readonly Track[] {
    return this.playlist.tracks;
  }

  get index(): number {
    return this.playlist.index;
  }

  get source(): TrackSource {
    return this.track?.source ?? 'demo';
  }

  get engine(): Engine {
    return this.source === 'youtube' ? this.yt : this.demo;
  }

  get playing(): boolean {
    return !!this.track && this.engine.playing;
  }

  get position(): number {
    return this.track ? this.engine.position : 0;
  }

  get duration(): number {
    if (!this.track) return 0;
    return this.engine.duration || this.track.duration || 0;
  }

  get progress(): number {
    const d = this.duration;
    return d ? clamp(this.position / d, 0, 1) : 0;
  }

  get level(): number {
    return this.track ? this.engine.level : 0;
  }

  get pitch(): number {
    return this.#pitch;
  }

  setQueue(tracks: readonly Track[], startIndex = 0): void {
    this.playlist.set(tracks, startIndex);
    this.emit('queue', this.playlist.tracks, this.playlist.index);
  }

  /**
   * Prepare a track without starting it. The caller drives the visual record
   * swap and then calls {@link play} when the needle lands.
   */
  async prepare(track: Track, index: number | null = null): Promise<void> {
    /* Stop whatever is playing first, so the two engines never overlap. */
    if (this.track) this.engine.pause();
    if (index !== null) this.playlist.index = index;
    this.track = track;
    this.emit('trackchange', track, this.playlist.index);

    const effective = this.muted ? 0 : this.volume;
    if (track.source === 'youtube' && track.videoId) {
      this.yt.setVolume(effective);
      await this.yt.load(track.videoId, { autoplay: false });
    } else {
      await this.demo.ensure().catch(() => undefined);
      this.demo.setVolume(effective);
      this.demo.load(track.demo);
    }
    this.emit('durationchange', this.duration);
    this.emit('timeupdate', 0, this.duration);
  }

  async play(): Promise<void> {
    if (this.track) await this.engine.play();
  }

  pause(): void {
    if (this.track) this.engine.pause();
  }

  seek(sec: number): void {
    if (this.track) this.engine.seek(clamp(sec, 0, this.duration || sec));
  }

  seekBy(delta: number): void {
    this.seek(this.position + delta);
  }

  seekTo(fraction: number): void {
    this.seek(clamp(fraction, 0, 1) * this.duration);
  }

  setVolume(v: number): void {
    this.volume = clamp(v, 0, 1);
    this.#pushVolume();
  }

  setMuted(m: boolean): void {
    this.muted = m;
    this.#pushVolume();
  }

  /** Pitch fader, in percent. Reports back the rate the engine actually used. */
  setPitch(pct: number): PitchResult {
    this.#pitch = clamp(pct, -8, 8);
    const requested = 1 + this.#pitch / 100;
    this.demo.setRate(requested);
    const applied = this.source === 'youtube' ? this.yt.setRate(requested) : requested;
    return { requested, applied };
  }

  destroy(): void {
    this.demo.destroy();
    this.yt.destroy();
  }

  #pushVolume(): void {
    const effective = this.muted ? 0 : this.volume;
    this.demo.setVolume(effective);
    this.yt.setVolume(effective);
    this.emit('volume', this.volume, this.muted);
  }

  #bridge(name: TrackSource, engine: Engine): void {
    bridgeEngine(engine, {
      active: () => this.source === name,
      time: (t) => this.emit('timeupdate', t, this.duration),
      duration: (d) => this.emit('durationchange', d),
      play: () => this.emit('play'),
      pause: () => this.emit('pause'),
      error: (msg) => this.emit('error', msg),
      ended: () => this.#onEnded(),
    });
  }

  #onEnded(): void {
    this.emit('ended', this.track);
    if (this.playlist.repeat === 'one') {
      this.seek(0);
      void this.play();
      return;
    }
    this.emit('wantnext');
  }
}
